import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PageHero from '../components/layout/PageHero';
import {
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

const DEFAULT_INTRO =
  'For collaboration, admissions inquiries, or research-related questions, please leave a message below and we will respond as soon as possible.';

const Contact: React.FC = () => {
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    role: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);

    if (!form.name || !form.email || !form.message) {
      setFeedback('Please provide your name, email, and message.');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/contact-messages', form);
      setFeedback('Your message has been sent successfully. We will contact you soon.');
      setForm({
        name: '',
        email: '',
        organization: '',
        role: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error(error);
      setFeedback('Submission failed. Please try again later or contact us by email.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    api.get<{ contact: string }>('/page-intros').then((res) => {
      const v = res.data?.contact?.trim();
      if (v) setIntro(v);
    }).catch(() => {});
  }, []);

  return (
    <section className="bg-surface text-on-surface min-h-full">
      <PageHero title="Contact" intro={intro} sectionLabel="Get in Touch" />
      <main className="mx-auto max-w-screen-2xl px-4 md:px-8 pb-20 pt-12 w-full">

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <section className="um-card lg:col-span-8 bg-surface-container-low p-8 md:p-12">
            <h2 className="mb-8 text-3xl font-semibold leading-tight text-primary md:text-4xl">Send a Message</h2>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-on-surface">Name</label>
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    className="um-input px-4 py-3"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-on-surface">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className="um-input px-4 py-3"
                    placeholder="Your email address"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-on-surface">Organization</label>
                  <input
                    name="organization"
                    type="text"
                    value={form.organization}
                    onChange={handleChange}
                    className="um-input px-4 py-3"
                    placeholder="e.g. University of Macau"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-on-surface">Role / Title</label>
                  <input
                    name="role"
                    type="text"
                    value={form.role}
                    onChange={handleChange}
                    className="um-input px-4 py-3"
                    placeholder="e.g. PhD student"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-on-surface">Subject</label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  className="um-input w-full appearance-none px-4 py-3"
                >
                  <option value="">Research Collaboration</option>
                  <option value="admissions">Admissions & Recruitment</option>
                  <option value="media">Media Inquiry</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-on-surface">Message</label>
                <textarea
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={handleChange}
                  className="um-input w-full resize-none px-4 py-3"
                  placeholder="Please describe your request..."
                />
              </div>

              <div className="pt-4 flex items-center justify-between gap-6">
                <p className="max-w-sm text-xs text-on-surface-variant">
                  By submitting, you agree to the handling of your information according to our privacy policy.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="um-btn-primary px-8 py-3 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
              {feedback && (
                <p className="text-sm text-on-surface-variant">{feedback}</p>
              )}
            </form>
          </section>

          <aside className="lg:col-span-4 space-y-8">
            <div className="um-card bg-surface-container-lowest p-8">
              <h3 className="mb-6 text-3xl font-semibold leading-tight text-primary">Contact Details</h3>
              <ul className="space-y-6 text-sm text-on-surface">
                <li className="flex items-start gap-4">
                  <BuildingOfficeIcon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <strong className="mb-1 block text-primary">Affiliation</strong>
                    <p className="text-on-surface-variant">
                      Institute of Applied Physics and Materials Engineering
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <EnvelopeIcon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <strong className="mb-1 block text-primary">Email (@um.edu.mo)</strong>
                    <a href="mailto:huipan@um.edu.mo" className="text-on-surface-variant hover:text-primary">
                      huipan
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <PhoneIcon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <strong className="mb-1 block text-primary">Tel</strong>
                    <p className="text-on-surface-variant">+853 8822 4427</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPinIcon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <strong className="mb-1 block text-primary">Office</strong>
                    <p className="text-on-surface-variant">
                      N23 Research Building, Room 4003
                      <br />
                      University of Macau
                      <br />
                      Avenida da Universidade
                      <br />
                      Taipa, Macau, China
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <GlobeAltIcon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <strong className="mb-2 block text-primary">Personal Website</strong>
                    <ul className="space-y-1 text-on-surface-variant">
                      <li><a href="https://scholar.google.com.sg/citations?user=vmGv38sAAAAJ&hl=en" target="_blank" rel="noreferrer" className="hover:text-primary">Google Scholar</a></li>
                      <li><a href="https://www.webofscience.com/wos/?mode=Nextgen&path=%2Fwos%2Fauthor%2Frid%2FJAC-6641-2023&IsProductCode=Yes&Init=Yes&DestApp=UA&Func=Frame&action=transfer&SrcApp=CR&SID=EUW1ED0ECBZvcXaepV1rKXVhtmk2R" target="_blank" rel="noreferrer" className="hover:text-primary">Publons</a></li>
                      <li><a href="https://orcid.org/0000-0002-6515-4970" target="_blank" rel="noreferrer" className="hover:text-primary">Orcid</a></li>
                      <li><a href="https://www.scopus.com/authid/detail.uri?authorId=57192713115" target="_blank" rel="noreferrer" className="hover:text-primary">Scopus</a></li>
                      <li><a href="https://iapme.um.edu.mo/wp-content/uploads/2020/12/HuiPan_CV-202011.pdf" target="_blank" rel="noreferrer" className="hover:text-primary">Curriculum Vitae</a></li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </main>
    </section>
  );
};

export default Contact;

